import React, { useState } from 'react';
import { Button, Divider, Image, Tag, Tooltip } from 'antd';
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import QuoteStore from '../stores/QuoteStore';
import * as api from '@partsbadger/utils';

import { Upload3dFile, UploadDrawing } from './UploadParts';
import { observer } from 'mobx-react';
import { QuoteLineItemProps } from '@partsbadger/types';
import ThreeDViewerModal from './ThreeDViewerModal';

import { FileAnalysisAlert } from './FileAnalysisAlert';
import { isBlocking3dFailure, isPartial3dAnalysis } from '../utils/partGeometry';

/**
 * ---------------------------------------------------------------------------
 * Part Header — normalized design tokens
 * ---------------------------------------------------------------------------
 * This is the single source of truth for the "3D File / 2D File" status row
 * shown on every quote line item. Previously this same information was
 * rendered with two different visual systems in the codebase (a large
 * vertical preview box with a red "Upload 3D File" button + a "View 2D file"
 * PDF-icon link, vs. a compact inline row) which made the header look
 * different from item to item. Everything below funnels through the same
 * label/row/link/button shapes so both file types render identically.
 */

// Fixed label column width so "3D File:" / "2D File:" values start at the
// exact same x-position regardless of label text length.
const FILE_LABEL_WIDTH = 56;
// Fixed row height so a row showing a short Tag lines up with a row showing
// an Upload button or an inline error Alert trigger.
const FILE_ROW_MIN_HEIGHT = 24;

const fileLabelStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    height: FILE_ROW_MIN_HEIGHT,
    width: FILE_LABEL_WIDTH,
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 600,
};

/**
 * One row of the Part Header's file status block: a fixed-width label
 * followed by whatever state content applies (missing / uploaded / error).
 */
const FileRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-center" style={{ minHeight: FILE_ROW_MIN_HEIGHT, columnGap: 2 }}>
        <span className="part-header-file-label" style={fileLabelStyle}>
            {label}
        </span>
        {children}
    </div>
);

/**
 * "File already loaded" action — identical shape for 3D and 2D: the filename
 * as a link (so the user knows exactly which file is attached) with a single
 * "preview" icon (eye), since clicking it always opens a viewer. Using one
 * icon/color for this action everywhere removes the earlier mismatch between
 * a paperclip Tag (3D) and a PDF-icon link (2D).
 */
const FileAttachmentLink = ({ filename, onClick }: { filename: string; onClick: (e: React.MouseEvent) => void }) => (
    <Button
        type="link"
        size="small"
        className="p-0"
        icon={<EyeOutlined />}
        onClick={onClick}
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            maxWidth: 220,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        }}
        title={filename}
    >
        {filename}
    </Button>
);

/**
 * Compact, horizontal Part Header: thumbnail + 3D/2D file status (or upload
 * controls when a file is still missing), rendered inline in the card
 * header. `readOnly` renders just the thumbnail (used in the collapsed list
 * view) — no upload actions, no file-status rows — since that view is a
 * dense summary, not an editing surface.
 */
interface PartFilesHeaderProps {
    item: QuoteLineItemProps;
    readOnly?: boolean;
    header?: React.ReactNode;
    footer?: React.ReactNode;
    fileActions?: React.ReactNode;
}

export const PartFilesHeader = observer(
    ({ item, readOnly = false, header, footer, fileActions }: PartFilesHeaderProps) => {
        const product = item.product;
        const direct3dModelUrl = product?.attachments?.file3d?.file_3djs ?? null;

        const [url, setUrl] = useState<string | null>(null);
        const [modelLoading, setModelLoading] = useState(false);
        const [modalVisible, setModalVisible] = useState(false);
        const [drawingUploadInProgress, setDrawingUploadInProgress] = useState(false);
        const [threeDUploadInProgress, setThreeDUploadInProgress] = useState(false);

        const open3dViewer = async (e?: React.MouseEvent) => {
            e?.stopPropagation();
            setModalVisible(true);
            if (url) return;
            if (direct3dModelUrl) {
                setUrl(direct3dModelUrl);
                return;
            }
            setModelLoading(true);
            try {
                const data = await api.get(`/customer/quote/${QuoteStore.quote_uid}/threed-signed-url/${item.uid}`);
                setUrl(data.url);
            } catch (error) {
                console.log(error);
            } finally {
                setModelLoading(false);
            }
        };

        const open2dViewer = (fileUrl?: string, e?: React.MouseEvent) => {
            e?.stopPropagation();
            if (!fileUrl) {
                return;
            }

            const win = window.open(fileUrl, '_blank', 'noopener,noreferrer,width=800,height=800,left=200,top=200');
            if (win) {
                win.opener = null;
            }
        };

        const { analysis_status, has_file_3d, has_file_2d, thumbnail, attachments } = product;

        // A failed detail analysis only blocks the file when there is no geometry for it.
        const analyzer_3d_failure = isBlocking3dFailure(product);
        const partial_3d_analysis = isPartial3dAnalysis(product);
        const filesEditable = !!item.master_product_details.editable;

        // Staff requote (and any editable staff line): mirror 2D — remove then upload.
        // Customer Portal keeps existing healthy 3D view-only on requote (PTB-6759).
        const canUpload3d =
            (!has_file_3d && !QuoteStore.isRequote) || (QuoteStore.isRequote && filesEditable && !has_file_3d);
        const canRemove3d = QuoteStore.for_staff && filesEditable && has_file_3d && !analyzer_3d_failure;

        const remove3dFile = (e: React.MouseEvent) => {
            e.stopPropagation();
            void QuoteStore.updateLineItemFiles({
                id: item.id,
                product: { file_3d: null },
            });
        };

        const thumbnailSize = readOnly ? 40 : 56;

        return (
            <div className="flex items-start gap-3" onClick={e => e.stopPropagation()}>
                {thumbnail ? (
                    <Image
                        preview={false}
                        className="thumbnail-3d-file"
                        style={{
                            width: thumbnailSize,
                            height: thumbnailSize,
                            objectFit: 'contain',
                            cursor: 'pointer',
                            flexShrink: 0,
                        }}
                        alt={attachments?.file3d?.filename}
                        src={thumbnail}
                        onClick={e => (has_file_3d ? open3dViewer(e) : open2dViewer(attachments?.files_2d?.file, e))}
                    />
                ) : null}

                <div className="flex flex-col">
                    {header ? <div style={{ marginBottom: 4 }}>{header}</div> : null}

                    {footer ? <div style={{ marginBottom: 6 }}>{footer}</div> : null}
                    {!readOnly && (
                        <div className="flex flex-row flex-wrap items-center gap-x-1 gap-y-1">
                            {/* 3D File always renders before 2D File — fixed order across every state. */}
                            <FileRow label="3D File:">
                                {!has_file_3d ? (
                                    <>
                                        {!threeDUploadInProgress ? (
                                            <Tag color="warning" className="mb-0">
                                                Missing 3D file
                                            </Tag>
                                        ) : null}
                                        {canUpload3d ? (
                                            <Upload3dFile
                                                item={item}
                                                buttonType="compact"
                                                buttonText="Upload"
                                                onUploadStateChange={setThreeDUploadInProgress}
                                            />
                                        ) : null}
                                    </>
                                ) : analyzer_3d_failure ? (
                                    <FileAnalysisAlert
                                        fileType="3D"
                                        item={item}
                                        analysisStatus={analysis_status.file_3d}
                                        variant="inline"
                                    />
                                ) : attachments?.file3d?.filename ? (
                                    <>
                                        <FileAttachmentLink
                                            filename={attachments.file3d.filename}
                                            onClick={open3dViewer}
                                        />
                                        {partial_3d_analysis ? (
                                            <Tooltip title="The part's geometry and price are ready, but some 3D details could not be generated.">
                                                <Tag color="warning" className="mb-0 ml-1">
                                                    Partial analysis
                                                </Tag>
                                            </Tooltip>
                                        ) : null}
                                        {canRemove3d ? (
                                            <Tooltip title="Remove 3D file">
                                                <Button
                                                    type="link"
                                                    danger
                                                    size="small"
                                                    className="p-0 ml-1"
                                                    icon={<DeleteOutlined />}
                                                    aria-label="Remove 3D file"
                                                    onClick={remove3dFile}
                                                    style={{ display: 'inline-flex', alignItems: 'center' }}
                                                />
                                            </Tooltip>
                                        ) : null}
                                    </>
                                ) : null}
                            </FileRow>

                            <Divider
                                type="vertical"
                                style={{ height: 20, margin: '0 12px', borderColor: '#595959', flexShrink: 0 }}
                            />

                            <div>
                                <FileRow label="2D File:">
                                    {!has_file_2d && !drawingUploadInProgress ? (
                                        <Tag color="warning" className="mb-0">
                                            Missing 2D file
                                        </Tag>
                                    ) : null}
                                    <UploadDrawing
                                        item={item}
                                        readOnly={!item.master_product_details.editable}
                                        compact
                                        onUploadStateChange={setDrawingUploadInProgress}
                                    />
                                </FileRow>
                            </div>
                            {fileActions ? (
                                <>
                                    <Divider
                                        type="vertical"
                                        style={{ height: 20, margin: '0 12px', borderColor: '#595959', flexShrink: 0 }}
                                    />
                                    <div className="flex items-center" style={{ height: FILE_ROW_MIN_HEIGHT, gap: 12 }}>
                                        {fileActions}
                                    </div>
                                </>
                            ) : null}
                        </div>
                    )}
                </div>

                <ThreeDViewerModal
                    url={url}
                    visible={modalVisible}
                    onClose={() => {
                        setModalVisible(false);
                        setUrl(null);
                    }}
                    loading={modelLoading}
                />
            </div>
        );
    }
);
